import { useState, useRef, type DragEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { X, Upload, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { useToast } from "@/lib/toast";

interface ImportResult {
  inserted: number;
  skipped: number;
  errors: { row: number; reason: string }[];
  total: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  /** Endpoint to POST to, ex: "/api/contacts/import" */
  endpoint: string;
  /** Human-readable label used in the title + sample column hint */
  entityLabel: string;
  /** Columns we expect - used in the help text + sample download. */
  columns: { key: string; required?: boolean; example?: string; help?: string }[];
  /** Optional row coercer (eg. parse number columns from string). */
  coerceRow?: (raw: Record<string, string>) => Record<string, unknown>;
}

export function CsvImporter({
  open, onClose, onDone, endpoint, entityLabel, columns, coerceRow,
}: Props) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rawText, setRawText] = useState("");
  const [parsed, setParsed] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const importMut = useMutation({
    mutationFn: async () => {
      if (!parsed) throw new Error("nothing to import");
      const rows = parsed.rows.map((row) => {
        const obj: Record<string, string> = {};
        parsed.headers.forEach((h, i) => {
          const v = (row[i] ?? "").trim();
          if (v) obj[h] = v;
        });
        return coerceRow ? coerceRow(obj) : obj;
      });
      const res = await api.post<ImportResult>(endpoint, { rows });
      return res;
    },
    onSuccess: (res) => {
      setResult(res);
      onDone();
      if (res.errors.length === 0) {
        toast.success(
          `${entityLabel} imported`,
          `${res.inserted} added · ${res.skipped} skipped`,
        );
      } else {
        toast.warn(
          `${entityLabel} imported with errors`,
          `${res.inserted} ok · ${res.errors.length} failed · ${res.skipped} skipped`,
        );
      }
    },
    onError: (e) => {
      toast.error(`${entityLabel} import failed`, e instanceof Error ? e.message : String(e));
    },
  });

  function handleText(text: string) {
    setRawText(text);
    setParseError(null);
    setResult(null);
    try {
      const out = parseCsv(text);
      setParsed(out);
    } catch (e) {
      setParsed(null);
      setParseError(e instanceof Error ? e.message : String(e));
    }
  }

  function handleFile(file: File) {
    if (file.size > 5 * 1024 * 1024) {
      setParseError("File too large - max 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => handleText(String(reader.result ?? ""));
    reader.onerror = () => setParseError("Could not read file");
    reader.readAsText(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function downloadSample() {
    const csv = [
      columns.map((c) => c.key).join(","),
      columns.map((c) => `"${c.example ?? ""}"`).join(","),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entityLabel.toLowerCase()}-sample.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function reset() {
    setRawText("");
    setParsed(null);
    setResult(null);
    setParseError(null);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40">
      <div className="w-full max-w-3xl bg-brand-surface shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-brand-surface flex items-center justify-between border-b border-slate-200 px-5 py-3 z-10">
          <h2 className="font-semibold">Import {entityLabel} from CSV</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Help block */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1.5">
            <div className="font-medium text-slate-900">Expected columns</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {columns.map((c) => (
                <div key={c.key} className="flex items-baseline gap-1">
                  <code className="font-mono text-[11px] bg-white border border-slate-200 rounded px-1">{c.key}</code>
                  {c.required && <span className="text-[10px] text-red-600">*</span>}
                  {c.help && <span className="text-[10px] text-slate-500 truncate">- {c.help}</span>}
                </div>
              ))}
            </div>
            <div className="pt-1">
              <button
                type="button"
                onClick={downloadSample}
                className="text-xs underline-offset-2 hover:underline text-slate-600"
              >
                Download a sample CSV ↓
              </button>
            </div>
          </div>

          {!result && (
            <>
              {/* Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "rounded-md border-2 border-dashed p-6 text-center text-sm cursor-pointer transition",
                  dragging
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-300 hover:border-slate-400",
                )}
              >
                <Upload className="h-5 w-5 mx-auto mb-1 text-slate-400" />
                <div className="text-slate-700">
                  Drop a <code className="text-[11px] bg-white border border-slate-200 rounded px-1">.csv</code> file here, or click to pick one
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Max 5 MB · UTF-8 · comma-separated</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                  }}
                />
              </div>

              <div className="text-[11px] text-slate-500 -my-1 text-center">or paste below</div>

              <textarea
                rows={6}
                placeholder="name,area,type,bedrooms,monthly_price_idr&#10;Villa Mantra,Canggu,villa,3,40000000"
                value={rawText}
                onChange={(e) => handleText(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs font-mono"
              />

              {parseError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  {parseError}
                </div>
              )}

              {parsed && (
                <div className="rounded-md border border-slate-200">
                  <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                    <div className="text-xs text-slate-700">
                      <strong>{parsed.rows.length}</strong> rows · <strong>{parsed.headers.length}</strong> columns
                    </div>
                    <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline">
                      Clear
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-72">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                          {parsed.headers.map((h) => (
                            <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.rows.slice(0, 50).map((row, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            {parsed.headers.map((_, j) => (
                              <td key={j} className="px-2 py-1 text-slate-700 truncate max-w-[140px]">
                                {row[j] ?? ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsed.rows.length > 50 && (
                      <div className="px-2 py-2 text-[11px] text-slate-500 text-center bg-slate-50">
                        … {parsed.rows.length - 50} more rows
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <Button
                  type="button"
                  onClick={() => importMut.mutate()}
                  disabled={!parsed || parsed.rows.length === 0 || importMut.isPending}
                >
                  {importMut.isPending ? "Importing…" : `Import ${parsed?.rows.length ?? 0} row(s)`}
                </Button>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Import complete</div>
                  <div className="text-xs mt-0.5">
                    <strong>{result.inserted}</strong> inserted ·{" "}
                    <strong>{result.skipped}</strong> skipped (duplicates) ·{" "}
                    <strong>{result.errors.length}</strong> errors ·{" "}
                    <strong>{result.total}</strong> total
                  </div>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50/30 max-h-48 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-red-200 text-xs font-medium text-red-700 bg-red-50 sticky top-0">
                    {result.errors.length} row error(s)
                  </div>
                  <ul className="text-xs">
                    {result.errors.map((e, i) => (
                      <li key={i} className="px-3 py-1 border-b border-red-100 flex items-baseline gap-2">
                        <FileText className="h-3 w-3 text-red-500 shrink-0" />
                        <span className="text-red-900">Row {e.row + 1}:</span>
                        <span className="text-red-700">{e.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={reset}>Import more</Button>
                <Button type="button" onClick={onClose}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minimal CSV parser - handles quoted fields with embedded commas/newlines.
// ---------------------------------------------------------------------------

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  // Normalize line endings
  const src = text.replace(/\r\n/g, "\n").trim();
  if (!src) throw new Error("Empty CSV");

  const rows: string[][] = [];
  let i = 0;
  let cell = "";
  let row: string[] = [];
  let inQuotes = false;

  while (i < src.length) {
    const ch = src[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cell += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ",") {
      row.push(cell);
      cell = "";
      i++;
      continue;
    }
    if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      i++;
      continue;
    }
    cell += ch;
    i++;
  }
  // last cell
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  if (rows.length === 0) throw new Error("No rows found");
  const headers = rows[0]!.map((h) => h.trim());
  const data = rows.slice(1).filter((r) => r.some((c) => c.trim().length > 0));
  return { headers, rows: data };
}
